import type { LearnerState } from '@/types/learner';

/**
 * Seed learner state — drives the demo from a coherent starting point.
 *
 * Alex sits on M1 L4 with Priya (the rate-shopper) just unlocked, and one
 * prior Priya attempt in session_history so the dashboard's "Past sessions"
 * list renders. Tyler (the warm over-agreer) is already mastered so the
 * scorecard shows a non-trivial Personas Mastered tile.
 *
 * DEMO: all four personas are unlocked so the full lineup — including Eleanor,
 * the suitability-inversion room — is explorable without grinding module
 * progression. (Eleanor's scenario still carries unlock_requires:'module:3'
 * as the production gating rule; this just pre-unlocks her for the demo seed.)
 */
export const SEED_LEARNER: LearnerState = {
  learner_id: 'demo-learner',
  display_name: 'Alex Rivera',

  current_module: 1,
  current_lesson: 4,
  lesson_progress_pct: 0.12,
  modules_complete: 0,

  personas_unlocked: ['dan', 'rita', 'maya', 'tyler'],
  personas_mastered: [],

  strengths: [
    { skill: 'discovery_questions', confidence: 0.72 },
    { skill: 'composure', confidence: 0.68 },
  ],
  focus_areas: [
    { skill: 'opening_hook', confidence: 0.44 },
    { skill: 'proof_to_pain', confidence: 0.49 },
  ],

  saved_playbook: [],
  session_history: [
    {
      id: 'sess-dan-001',
      persona_id: 'dan',
      attempt_number: 1,
      started_at: '2026-01-15T15:22:00Z',
      duration_s: 540,
      outcome: 'not_closed',
      score: 64,
      milestones_hit: [
        'conduct_needs_assessment_before_defending_fee',
        'provide_data_backed_value_justification',
      ],
      transcript: [
        {
          speaker: 'persona',
          text: "Every agent I've talked to says they 'go above and beyond' — that's not an answer, that's a slogan. What specifically do you do that the two-percent guy on Zillow doesn't?",
          at_s: 5,
        },
        {
          speaker: 'learner',
          text: "Fair question — before I answer that, can I ask what's actually driving it for you? I'd rather solve the real thing than the first thing.",
          at_s: 14,
        },
        {
          speaker: 'persona',
          text: "I've bought property before. I know how this works. You plug my criteria into the MLS, you book showings, you write up the offer I tell you to write. That's not worth three percent of a three-hundred-forty-thousand-dollar house.",
          at_s: 48,
        },
        {
          speaker: 'learner',
          text: "Honestly, we're just better at this than the alternatives — most people are glad they made the switch.",
          at_s: 120,
        },
        {
          speaker: 'persona',
          text: "Look, I'm not trying to be a jerk about it. I'm self-employed — every dollar of margin matters to me. If you can't sharpen your pencil at all, I've got two other agents who will.",
          at_s: 138,
        },
      ],
      moments: [
        {
          type: 'win',
          title: 'You led with a real question',
          quote:
            "Fair question — before I answer that, can I ask what's actually driving it for you? I'd rather solve the real thing than the first thing.",
          at_s: 14,
          coach_note:
            "You asked before you answered — that's how you earn the room instead of defending. The whole conversation opens up from there.",
          coach_note_id: 'demo-win-discovery',
        },
        {
          type: 'growth',
          title: 'You retreated to a slogan',
          quote:
            "Honestly, we're just better at this than the alternatives — most people are glad they made the switch.",
          at_s: 120,
          coach_note:
            "You had the opening and fell back to a generic claim. Anchor to their specifics — what they actually said — not to 'better.'",
          coach_note_id: 'demo-growth-slogan',
        },
      ],
    },
  ],
};

// Re-exports so screens that import from `@/seed` get the data they
// need without having to remember which subfile owns each piece.
export {
  SCENARIOS,
  SCENARIOS_BY_PERSONA,
  SCENARIO_ORDER,
  SCENARIO_DAN,
  SCENARIO_RITA,
  SCENARIO_MAYA,
  SCENARIO_TYLER,
} from './scenarios';
export {
  MODULES_SEED,
  TOTAL_MODULES,
  findModule,
  findLesson,
  type ModuleMeta,
  type LessonMeta,
} from './modules';
export { LESSONS, LESSON_M1_L4 } from './lessons';
export {
  VERTICALS_SEED,
  VERTICAL_ORDER,
  type Vertical,
  type VerticalKey,
  type VerticalColor,
} from './verticals';
